import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import { basicApiAsyncThunkCreator } from '../../services/apiAsyncThunkCreator';
import { ThunkAPI } from '../rootReducer';
import { showErrorMessage, showSuccessMessage } from '../notifications';
import { ApiError, HttpMethods, SERVICES } from '../../services/api';
import { actions as AccountListActions } from './list';
import { AccountStates, IAccount } from './account';
import { push } from 'connected-react-router';
import { isExternalClient, isIncrowdClient } from '../../utils/clients';

export interface ICreateAccountDTO {
  id?: string;
  name: string;
  accessType: string;
  address?: string;
  logo?: string;
  renewalAt?: Date;
  seatsUsed?: number;
  seatsAvailable?: number;
  createdAt?: Date;
  updatedAt?: Date;
  isActive?: boolean; // remove when removing plgexternal feature toggle
  billingContact?: string;
  responsesAllowed?: number;
  trialDays?: number;
  accountManager?: string;
  hubspotId?: string;
  region?: string;
  // use for Features.PlgExternal
  status?: AccountStates;
  paymentOptions?: string[];
  // end of use for Features.PlgExternal
  hubspotDealId?: string;
}

interface ICreateAccountResponse {
  id: string;
}

export const createAccount: AsyncThunk<ICreateAccountResponse, ICreateAccountDTO, ThunkAPI> = createAsyncThunk(
  'accounts/createAccount',
  async (accountData: ICreateAccountDTO, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<ICreateAccountResponse, ICreateAccountDTO>({
        endpoint: 'accounts',
        microservice: isIncrowdClient() || isExternalClient() ? SERVICES.USERS_SERVICE : SERVICES.USERS_SERVICE_V2,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(accountData, thunk);
      if ('id' in response) {
        const path = isExternalClient() ? 'users' : 'billing';
        thunk.dispatch(push(`/accounts/${response.id}/${path}`));
      } else {
        thunk.dispatch(AccountListActions.fetchAccountsList({}));
      }
      thunk.dispatch(showSuccessMessage('Account created successfully'));
      return response;
    } catch (err) {
      const apiError = err as ApiError;
      // Do not show toast when it's an userError (has an url)
      if (apiError?.userError) {
        return thunk.rejectWithValue(apiError);
      }

      thunk.dispatch(showErrorMessage(JSON.stringify(err) || 'An error occurred creating the new account'));
      return thunk.rejectWithValue(err);
    }
  }
);

export const editAccount: AsyncThunk<string, ICreateAccountDTO, ThunkAPI> = createAsyncThunk(
  'accounts/editAccount',
  async (accountData: ICreateAccountDTO, thunk) => {
    try {
      thunk.dispatch(AccountListActions.setEditingAccount(accountData.id || ''));
      await basicApiAsyncThunkCreator<void, ICreateAccountDTO>({
        endpoint: 'accounts',
        method: HttpMethods.Patch,
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        contentType: 'application/json'
      })(accountData, thunk);
      thunk.dispatch(AccountListActions.updateAccount(accountData as IAccount));
      thunk.dispatch(AccountListActions.clearEditingAccount());
      thunk.dispatch(showSuccessMessage('Account updated successfully'));
      return accountData.id || '';
    } catch (err) {
      thunk.dispatch(showErrorMessage(JSON.stringify(err) || 'An error occurred updating the account'));
      thunk.dispatch(AccountListActions.clearEditingAccount());
      return thunk.rejectWithValue(err);
    }
  }
);
